import { useState } from 'react';
import { motion } from 'framer-motion';
import { getShotImageUrl } from '@/services/shotService';
import { MULTI_FRAME } from '@/constants/shotSettings';
import { Camera, ChevronRight, Wand2 } from 'lucide-react';

export default function AnchorShotStrip({ anchor, projectId, onSelect }) {
  const [imgErrors, setImgErrors] = useState({});
  const movement = anchor?.planned_settings?.camera_movement;
  const multiFrame = movement ? MULTI_FRAME[movement] : null;
  const shots = anchor?.shots || [];

  const handleImgError = (shotId) => {
    setImgErrors(prev => ({ ...prev, [shotId]: true }));
  };

  return (
    <div
      className="my-3 mx-1 rounded-xl border border-[var(--border-subtle)] bg-[rgba(17,17,19,0.8)] backdrop-blur-sm overflow-hidden cursor-pointer hover:border-[rgba(212,168,83,0.4)] transition-colors group"
      onClick={() => onSelect?.(anchor)}
      data-testid={`anchor-strip-${anchor.id}`}
    >
      <div className="px-3 py-2 flex items-center justify-between border-b border-[rgba(255,255,255,0.5)]">
        <div className="flex items-center gap-2 min-w-0">
          <Camera size={12} className="text-[var(--color-gold-400)] shrink-0" />
          <span className="text-[10px] font-medium text-[var(--text-primary)] truncate">
            {anchor.planned_settings?.shot_type?.replace('-', ' ') || 'Shot'}
          </span>
          {movement && movement !== 'static' && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--bg-overlay)] text-[var(--text-muted)]">
              {movement.replace('-', ' ')}
            </span>
          )}
          {anchor.planned_settings?.camera_angle && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--bg-overlay)] text-[var(--text-muted)]">
              {anchor.planned_settings.camera_angle.replace('-', ' ')}
            </span>
          )}
        </div>
        <ChevronRight size={12} className="text-[var(--text-disabled)] group-hover:text-[var(--color-gold-400)] transition-colors shrink-0" />
      </div>

      <div className="p-2 flex gap-2 overflow-x-auto">
        {shots.length > 0 ? (
          shots.map((shot, idx) => (
            <div key={shot.id || idx} className="relative shrink-0">
              {multiFrame && multiFrame.labels[idx] && (
                <p className="text-[8px] text-[var(--text-disabled)] text-center mb-0.5 font-medium">
                  {multiFrame.labels[idx]}
                </p>
              )}
              <div className="w-28 aspect-video rounded-lg overflow-hidden bg-[var(--bg-muted)] relative">
                {shot.status === 'completed' && !imgErrors[shot.id] ? (
                  <img
                    src={getShotImageUrl(anchor.id, shot.id)}
                    alt={`Shot ${idx + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"

                    onError={() => handleImgError(shot.id)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Camera size={14} className="text-[var(--text-disabled)]" />
                  </div>
                )}
              </div>
              {idx < shots.length - 1 && multiFrame && multiFrame.count > 1 && (
                <div className="absolute -right-1.5 top-1/2 -translate-y-1/2 z-10">
                  <ChevronRight size={8} className="text-[var(--text-disabled)]" />
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="flex items-center gap-2 px-2 py-3 w-full">
            <Wand2 size={12} className="text-[var(--color-gold-400)]" />
            <span className="text-[10px] text-[var(--text-muted)]">
              {anchor.planned_settings ? 'Ready to generate' : 'Configure shot settings'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
